import { useState, useRef, useEffect } from 'react'
import { ChevronDown, Trash2, Edit2, MoreHorizontal, Plus, Loader2, X, CheckCircle2, Lock, CalendarClock, User, Check, Search } from 'lucide-react'
import { useApp } from '@/context/AppContext'
import { useAllUsers } from '@/lib/useAllUsers'
import { useClickOutside } from '@/lib/useClickOutside'
import { deptColor } from '@/lib/departments'
import { StatusBadge } from '@/components/StatusBadge'
import { SubtaskItem } from '@/components/SubtaskItem'
import { EditTaskModal } from '@/components/EditTaskModal'
import { RecurrenceControl } from '@/components/RecurrenceControl'
import { formatDate, picColor, parsePics, joinPics } from '@/lib/utils'
import type { Task, TaskStatus, TaskPriority } from '@/lib/types'

// Avatar PIC task + dropdown multi-select, persis seperti PIC subtask.
// Satu task boleh dipegang satu orang atau beberapa orang sekaligus; nama-namanya
// disimpan di satu kolom `assignee` digabung ';'. Kosong = ikut PIC project.
//
// Dropdown sengaja TETAP TERBUKA saat mencentang, supaya bisa memilih beberapa
// orang berturut-turut tanpa membuka ulang.
//
// Ganti PIC langsung berlaku, tanpa menanyakan alasan. Perpindahannya tetap
// tercatat di reassignment_log oleh server (kolom alasan dibiarkan kosong).
function TaskAssignee({ task, locked, canEdit }: { task: Task; locked: boolean; canEdit: boolean }) {
  const { getProject, updateTask } = useApp()
  const { users } = useAllUsers()
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const ref = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  // Kotak cari dikosongkan tiap kali ditutup, supaya membuka lagi tidak
  // menyisakan filter lama yang membuat daftar terlihat kosong tanpa sebab.
  const close = () => { setOpen(false); setQ('') }
  useClickOutside(ref, close, open)
  useEffect(() => { if (open) searchRef.current?.focus() }, [open])

  const project = getProject(task.projectId)
  const projectDept = project?.department ?? null
  const deptOf = new Map(users.map((u) => [u.username, u.department]))

  const selected = parsePics(task.assignee)
  const inherited = selected.length === 0
  // Yang ditampilkan: PIC task kalau ada, kalau tidak ikut PIC project (1 orang).
  const display = inherited ? (project?.pic ? [project.pic] : []) : selected

  const names = Array.from(new Set([
    ...selected,
    ...users.map((u) => u.username),
  ])).filter(Boolean).sort((a, b) => a.localeCompare(b))

  // Filter ketikan. Yang SUDAH tercentang selalu ikut tampil walau tidak cocok,
  // supaya mencabut orang tidak perlu menghapus ketikan lebih dulu.
  const qLower = q.trim().toLowerCase()
  const shown = qLower
    ? names.filter((n) =>
        n.toLowerCase().includes(qLower)
        || (deptOf.get(n) ?? '').toLowerCase().includes(qLower)
        || selected.some((sel) => sel.toLowerCase() === n.toLowerCase()))
    : names

  // Centang/hapus centang satu nama; dropdown dibiarkan terbuka.
  const toggle = (name: string) => {
    const next = selected.some((n) => n.toLowerCase() === name.toLowerCase())
      ? selected.filter((n) => n.toLowerCase() !== name.toLowerCase())
      : [...selected, name]
    updateTask(task.id, { assignee: joinPics(next) })
  }
  const clearToInherit = () => { updateTask(task.id, { assignee: null }); close() }

  const titleText = inherited
    ? (project?.pic ? `Ikut PIC project: ${project.pic}` : 'Set PIC task')
    : `PIC task: ${selected.join(', ')}`

  const Avatar = () =>
    display.length > 0 ? (
      <span className="flex items-center" style={{ opacity: inherited ? 0.65 : 1 }}>
        {display.slice(0, 3).map((n, idx) => (
          <span
            key={n}
            className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white ring-1 ring-white"
            style={{ backgroundColor: picColor(n), marginLeft: idx === 0 ? 0 : -6 }}
          >
            {n.charAt(0).toUpperCase()}
          </span>
        ))}
        {display.length > 3 && (
          <span className="ml-0.5 text-[9px] font-body text-[#8A8A85]">+{display.length - 3}</span>
        )}
      </span>
    ) : (
      <span className="w-5 h-5 rounded-full border border-dashed border-[#C8C7BF] flex items-center justify-center text-[#C8C7BF]">
        <User className="w-3 h-3" />
      </span>
    )

  // Tanpa hak ubah: avatar saja, seperti perilaku lama.
  if (locked || !canEdit) {
    if (display.length === 0) return null
    return <span className="flex-shrink-0" title={titleText}><Avatar /></span>
  }

  return (
    <div ref={ref} className="relative flex-shrink-0">
      <button
        onClick={(e) => { e.stopPropagation(); open ? close() : setOpen(true) }}
        title={titleText}
        aria-label="Atur PIC task"
        className="flex items-center"
      >
        <Avatar />
      </button>

      {open && (
        <div className="absolute right-0 top-[calc(100%+4px)] z-30 min-w-[220px] bg-white border border-[#E0DFD8] rounded-xl shadow-xl py-1.5 animate-fade-in">
            <p className="px-3 pb-1.5 text-[10px] uppercase tracking-wider text-[#C0BDB6] font-body font-semibold">
              PIC task · boleh lebih dari satu
            </p>
            <div className="px-2 pb-1.5 mb-1 border-b border-[#F2F1EC]">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-[#C0BDB6] pointer-events-none" />
                <input
                  ref={searchRef}
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    e.stopPropagation()
                    if (e.key === 'Escape') close()
                    // Enter memilih satu-satunya hasil — cari lalu langsung centang.
                    if (e.key === 'Enter' && shown.length === 1) { e.preventDefault(); toggle(shown[0]) }
                  }}
                  placeholder="Cari nama atau departemen"
                  className="w-full pl-7 pr-2.5 py-1.5 bg-white border border-[#E8E7E3] rounded-lg text-[11px] font-body text-[#1A1916] placeholder:text-[#C0BDB6] focus:outline-none focus:border-[#C7D2FE] transition-colors"
                />
              </div>
            </div>
            <div className="max-h-56 overflow-auto">
              {names.length === 0 && (
                <p className="px-3 py-1.5 text-[11px] text-[#AEADA8] font-body">Belum ada user terdaftar</p>
              )}
              {names.length > 0 && shown.length === 0 && (
                <p className="px-3 py-1.5 text-[11px] text-[#AEADA8] font-body">Tidak ada yang cocok dengan "{q.trim()}"</p>
              )}
              {shown.map((name) => {
                const on = selected.some((n) => n.toLowerCase() === name.toLowerCase())
                return (
                  <button
                    key={name}
                    type="button"
                    onClick={(e) => { e.stopPropagation(); toggle(name) }}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 text-[11px] font-body transition-colors ${
                      on ? 'bg-[#F4F3F0] text-[#1A1916] font-semibold' : 'text-[#6B6860] hover:bg-[#F4F3F0]'
                    }`}
                  >
                    <span
                      className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border transition-all"
                      style={{ borderColor: on ? '#6366F1' : '#C8C7BF', backgroundColor: on ? '#6366F1' : 'white' }}
                    >
                      {on && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                    </span>
                    <span
                      className="w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold text-white flex-shrink-0"
                      style={{ backgroundColor: picColor(name) }}
                    >
                      {name.charAt(0).toUpperCase()}
                    </span>
                    <span className="flex-1 text-left truncate">{name}</span>
                    {(() => {
                      const dep = deptOf.get(name)
                      if (!dep || dep === projectDept) return null
                      const c = deptColor(dep)
                      return (
                        <span
                          className="text-[9px] font-body font-semibold px-1 py-0.5 rounded border flex-shrink-0"
                          style={{ backgroundColor: c.bg, color: c.text, borderColor: c.border }}
                        >
                          {dep}
                        </span>
                      )
                    })()}
                  </button>
                )
              })}
            </div>
            {selected.length > 0 && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); clearToInherit() }}
                className="w-full flex items-center gap-2 px-3 py-1.5 mt-1 pt-1.5 border-t border-[#F2F1EC] text-[11px] font-body text-[#DC2626] hover:bg-[#FEF2F2] transition-colors"
              >
                <X className="w-3 h-3 flex-shrink-0" /> Kosongkan {project?.pic ? `(ikut PIC ${project.pic})` : ''}
              </button>
            )}
        </div>
      )}
    </div>
  )
}

// Tag prioritas yang bisa diklik untuk mengubah tingkat urgensi langsung dari
// baris task, tanpa membuka Edit Task. Dropdown, bukan klik-berputar, supaya
// tingkat yang diinginkan bisa dipilih sekali klik.
function TaskPriority({ task, locked, canEdit }: { task: Task; locked: boolean; canEdit: boolean }) {
  const { updateTask } = useApp()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useClickOutside(ref, () => setOpen(false), open)

  const p = PRIORITY[task.priority]
  const base = 'text-[10px] px-2 py-0.5 rounded-md font-body flex-shrink-0'

  if (locked || !canEdit) {
    return <span className={`${base} ${p.cls}`} title={`Prioritas ${p.label}`}>{p.label}</span>
  }

  return (
    <div ref={ref} className="relative flex-shrink-0">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v) }}
        title="Klik untuk ubah tingkat urgensi"
        className={`${base} ${p.cls} hover:brightness-95 transition-all`}
      >
        {p.label}
      </button>
      {open && (
        <div className="absolute right-0 top-[calc(100%+4px)] z-30 min-w-[130px] bg-white border border-[#E0DFD8] rounded-xl shadow-xl py-1.5 animate-fade-in">
          <p className="px-3 pb-1.5 mb-1 border-b border-[#F2F1EC] text-[10px] uppercase tracking-wider text-[#C0BDB6] font-body font-semibold">
            Urgensi
          </p>
          {PRIORITY_ORDER.map((key) => {
            const opt = PRIORITY[key]
            const on = key === task.priority
            return (
              <button
                key={key}
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  if (key !== task.priority) updateTask(task.id, { priority: key })
                  setOpen(false)
                }}
                className={`w-full flex items-center gap-2 px-3 py-1.5 text-[11px] font-body transition-colors ${
                  on ? 'bg-[#F4F3F0] text-[#1A1916] font-semibold' : 'text-[#6B6860] hover:bg-[#F4F3F0]'
                }`}
              >
                <span className="w-3 h-3 flex items-center justify-center flex-shrink-0">
                  {on && <Check className="w-3 h-3 text-[#6366F1]" strokeWidth={3} />}
                </span>
                <span className={`${base} ${opt.cls}`}>{opt.label}</span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

const PRIORITY_ORDER: TaskPriority[] = ['high', 'medium', 'low']

const PRIORITY: Record<string, { label: string; cls: string }> = {
  low:    { label: 'Low',    cls: 'text-[#64748B] bg-[#F1F5F9] border border-[#E2E8F0]' },
  medium: { label: 'Medium', cls: 'text-[#B45309] bg-[#FEF3C7] border border-[#FDE68A]' },
  high:   { label: 'High',   cls: 'text-[#DC2626] bg-[#FEF2F2] border border-[#FECACA]' },
}


export function TaskItem({ task, locked = false, canEdit = true }: { task: Task; locked?: boolean; canEdit?: boolean }) {
  const { deleteTask, getTaskSubtasks, createSubtask, deleteSubtask, getEffectiveTaskStatus, updateTask } = useApp()
  const subtasks = getTaskSubtasks(task.id)
  const effectiveStatus = getEffectiveTaskStatus(task.id)
  const isDone = effectiveStatus === 'done'

  // Task TANPA subtask: status manual. Tampilkan checkbox untuk men-done-kan
  // saat sedang In Progress / Done (Not Started → In Progress lewat Edit Task).
  const noSubtasks = subtasks.length === 0
  const showTaskCheckbox = !locked && canEdit && noSubtasks && (effectiveStatus === 'in-progress' || effectiveStatus === 'done')
  const toggleTaskDone = () => {
    if (effectiveStatus === 'done') {
      updateTask(task.id, { status: 'in-progress', tanggalSelesai: null })
    } else {
      updateTask(task.id, { status: 'done', tanggalSelesai: new Date().toISOString() })
    }
  }

  // Klik badge → maju ke status berikutnya. Hanya untuk task TANPA subtask
  // (task bersubtask statusnya turunan dari subtask, jadi tidak bisa diubah manual).
  const STATUS_NEXT: Record<TaskStatus, TaskStatus> = {
    'not-started': 'in-progress',
    'in-progress': 'done',
    'done': 'not-started',
  }
  const canCycleStatus = !locked && canEdit && noSubtasks
  const cycleStatus = () => {
    const next = STATUS_NEXT[effectiveStatus]
    updateTask(task.id, {
      status: next,
      tanggalSelesai: next === 'done' ? new Date().toISOString() : null,
    })
  }

  const [expanded, setExpanded] = useState(false)
  const [showMenu, setShowMenu] = useState(false)
  const [showEdit, setShowEdit] = useState(false)

  // Deadline task — klik untuk set/ubah/mundurkan langsung dari baris, tanpa
  // membuka Edit Task. Pola sama dengan deadline subtask & deadline project.
  const [editingDeadline, setEditingDeadline] = useState(false)
  const canEditDeadline = !locked && canEdit
  const saveDeadline = (value: string) => {
    updateTask(task.id, { dueDate: value || null })
    setEditingDeadline(false)
  }
  const overdue = !!task.dueDate && !isDone &&
    new Date(task.dueDate.slice(0, 10) + 'T00:00:00') < new Date(new Date().setHours(0, 0, 0, 0))

  // Tambah subtask inline
  const [addingSubtask, setAddingSubtask] = useState(false)
  const [newSubtaskTitle, setNewSubtaskTitle] = useState('')
  const [savingSubtask, setSavingSubtask] = useState(false)
  const addInputRef = useRef<HTMLInputElement>(null)

  const completedCount = subtasks.filter((s) => s.completed).length
  const hasContent = !!task.description || subtasks.length > 0 || addingSubtask

  useEffect(() => {
    if (addingSubtask && addInputRef.current) addInputRef.current.focus()
  }, [addingSubtask])

  const handleAddSubtask = async () => {
    const title = newSubtaskTitle.trim()
    if (!title || savingSubtask) return
    setSavingSubtask(true)
    try {
      await createSubtask(task.id, title)
      setNewSubtaskTitle('')
      // stay in add mode for quick multi-entry
    } catch {
      // notifikasi sudah ditampilkan via toast di context; biarkan input agar bisa dicoba lagi
    } finally {
      setSavingSubtask(false)
      addInputRef.current?.focus()
    }
  }

  return (
    <>
      <div
        className={`group bg-white border rounded-xl transition-all duration-150 ${
          isDone ? 'border-[#EEEEE9] opacity-55' : 'border-[#E8E7E3] hover:border-[#C8C7BF] hover:shadow-sm'
        }`}
      >
        <div className="flex items-center gap-3 px-4 py-3">
          {/* Checkbox done — hanya task tanpa subtask (status manual) */}
          {showTaskCheckbox && (
            <button
              onClick={(e) => { e.stopPropagation(); toggleTaskDone() }}
              className="flex-shrink-0 w-4 h-4 rounded border transition-all focus:outline-none"
              style={{
                borderColor: isDone ? '#16A34A' : '#C8C7BF',
                backgroundColor: isDone ? '#16A34A' : 'white',
              }}
              aria-label={isDone ? 'Tandai belum selesai' : 'Tandai selesai'}
              title={isDone ? 'Tandai belum selesai' : 'Tandai selesai'}
            >
              {isDone && (
                <svg viewBox="0 0 16 16" fill="none" className="w-full h-full p-0.5">
                  <path d="M3 8l3.5 3.5L13 5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>
          )}

          {/* Status badge — task tanpa subtask: klik untuk maju ke status berikutnya.
              Task bersubtask: status turunan, tidak bisa diklik. */}
          <div className="flex-shrink-0">
            {canCycleStatus ? (
              <StatusBadge status={effectiveStatus} onClick={cycleStatus} title="Klik untuk ubah status" />
            ) : (
              <StatusBadge status={effectiveStatus} />
            )}
          </div>

          {/* Title */}
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex-1 text-left min-w-0"
          >
            <span className="flex items-center gap-2 min-w-0">
              <span className={`truncate text-sm font-body font-medium ${isDone ? 'text-[#C0BDB6] line-through' : 'text-[#1A1916]'}`}>
                {task.title}
              </span>
              {subtasks.length > 0 && (
                <span className="text-[10px] font-body text-[#AEADA8] flex-shrink-0">
                  {completedCount}/{subtasks.length}
                </span>
              )}
            </span>
          </button>

          {/* PIC task — avatar + dropdown ganti PIC. Kosong = ikut PIC project. */}
          <TaskAssignee task={task} locked={locked} canEdit={canEdit} />

          {/* Tanda selesai */}
          {isDone && task.tanggalSelesai && (
            <span className="hidden sm:inline-flex items-center gap-1 text-[10px] font-body font-medium px-2 py-0.5 rounded-md bg-[#DCFCE7] text-[#15803D] border border-[#BBF7D0] flex-shrink-0"
              title={`Selesai ${formatDate(task.tanggalSelesai)}`}>
              <CheckCircle2 className="w-3 h-3" /> {formatDate(task.tanggalSelesai)}
            </span>
          )}

          {/* Rutin (repetitif) — di luar perhitungan % project */}
          <RecurrenceControl
            value={task.recurrence}
            onChange={(v) => updateTask(task.id, { recurrence: v })}
            disabled={locked || !canEdit}
          />

          {/* Priority */}
          <TaskPriority task={task} locked={locked} canEdit={canEdit} />

          {/* Tanggal dibuat */}
          <span
            className="text-[11px] text-[#C0BDB6] font-body hidden md:block flex-shrink-0"
            title={`Dibuat ${formatDate(task.createdAt)}`}
          >
            {formatDate(task.createdAt)}
          </span>

          {/* Deadline — di kanan tanggal dibuat. Klik untuk ubah atau mundurkan. */}
          {!isDone && (
            editingDeadline ? (
              <input
                type="date"
                autoFocus
                defaultValue={task.dueDate?.slice(0, 10) ?? ''}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveDeadline((e.target as HTMLInputElement).value)
                  if (e.key === 'Escape') setEditingDeadline(false)
                }}
                onBlur={(e) => saveDeadline(e.target.value)}
                className="flex-shrink-0 text-[10px] px-1.5 py-0.5 bg-white border border-[#C8C7BF] rounded-md font-body text-[#1A1916] focus:outline-none focus:border-[#1A1916]"
              />
            ) : task.dueDate ? (
              <button
                onClick={() => { if (canEditDeadline) setEditingDeadline(true) }}
                title={canEditDeadline ? 'Klik untuk ubah deadline task' : `Deadline ${formatDate(task.dueDate)}`}
                className={`hidden sm:inline-flex items-center gap-1 text-[10px] font-body font-medium px-2 py-0.5 rounded-md flex-shrink-0 transition-colors ${
                  overdue
                    ? `bg-[#FEF2F2] text-[#DC2626] border border-[#FECACA] ${canEditDeadline ? 'hover:bg-[#FDE2E2]' : ''}`
                    : `bg-[#F4F3F0] text-[#6B6860] ${canEditDeadline ? 'hover:bg-[#EEEEE9]' : ''}`
                } ${canEditDeadline ? '' : 'cursor-default'}`}
              >
                <CalendarClock className="w-3 h-3" />
                {overdue ? 'Telat · ' : ''}{formatDate(task.dueDate)}
              </button>
            ) : canEditDeadline ? (
              <button
                onClick={() => setEditingDeadline(true)}
                title="Set deadline task"
                className="hidden sm:inline-flex items-center gap-1 text-[10px] font-body font-medium px-2 py-0.5 rounded-md flex-shrink-0 text-[#6366F1] border border-dashed border-[#C7D2FE] hover:border-[#6366F1] transition-colors"
              >
                <CalendarClock className="w-3 h-3" /> Deadline
              </button>
            ) : null
          )}

          {/* Expand toggle */}
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-1 text-[#C8C7BF] hover:text-[#6B6860] flex-shrink-0 transition-colors"
          >
            <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
          </button>

          {/* Actions menu — disembunyikan saat terkunci atau tak boleh edit (assignee-only) */}
          {locked ? (
            <Lock className="w-3.5 h-3.5 text-[#C0BDB6] flex-shrink-0" />
          ) : !canEdit ? null : (
          <div className="relative flex-shrink-0">
            <button
              onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu) }}
              className="p-1.5 rounded-lg text-[#C8C7BF] hover:text-[#6B6860] hover:bg-[#F4F3F0] transition-colors"
            >
              <MoreHorizontal className="w-4 h-4" />
            </button>
            {showMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                <div className="absolute right-0 top-9 z-20 bg-white border border-[#E8E7E3] rounded-xl shadow-2xl shadow-black/10 py-1.5 w-40 animate-fade-in">
                  <button
                    onClick={() => { setShowEdit(true); setShowMenu(false) }}
                    className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-[#1A1916] hover:bg-[#F4F3F0] font-body transition-colors"
                  >
                    <Edit2 className="w-3.5 h-3.5 text-[#8A8A85]" />
                    Edit Task
                  </button>
                  <button
                    onClick={() => { deleteTask(task.id); setShowMenu(false) }}
                    className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-[#DC2626] hover:bg-[#FEF2F2] font-body transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Hapus
                  </button>
                </div>
              </>
            )}
          </div>
          )}
        </div>

        {/* Expanded section */}
        {expanded && (
          <div className="border-t border-[#F4F3F0]">
            {/* Description */}
            {task.description && (
              <div className="px-4 pt-3 pb-2">
                <p className="text-sm text-[#6B6860] font-body leading-relaxed whitespace-pre-wrap">
                  {task.description}
                </p>
              </div>
            )}

            {/* Subtasks section */}
            <div className="px-4 pt-2 pb-3">
              {/* Subtask list */}
              {subtasks.length > 0 && (
                <div className="mb-2 space-y-0.5">
                  {subtasks.map((s) => (
                    <SubtaskItem key={s.id} subtask={s} locked={locked} />
                  ))}
                </div>
              )}

              {/* Inline add form */}
              {!locked && canEdit && addingSubtask && (
                <div className="flex items-center gap-2 mb-2">
                  <input
                    ref={addInputRef}
                    type="text"
                    value={newSubtaskTitle}
                    onChange={(e) => setNewSubtaskTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleAddSubtask()
                      if (e.key === 'Escape') { setAddingSubtask(false); setNewSubtaskTitle('') }
                    }}
                    placeholder="Nama subtask..."
                    className="flex-1 text-xs px-3 py-2 bg-[#F8F7F3] border border-[#E0DFD8] rounded-lg font-body text-[#1A1916] placeholder:text-[#C8C7BF] focus:outline-none focus:border-[#1A1916] focus:bg-white transition-all"
                  />
                  <button
                    onClick={handleAddSubtask}
                    disabled={!newSubtaskTitle.trim() || savingSubtask}
                    className="px-3 py-2 bg-[#1A1916] text-white rounded-lg text-xs font-medium font-body hover:bg-[#2A2A28] disabled:opacity-40 transition-all"
                  >
                    {savingSubtask ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Simpan'}
                  </button>
                  <button
                    onClick={() => { setAddingSubtask(false); setNewSubtaskTitle('') }}
                    className="p-2 text-[#C8C7BF] hover:text-[#6B6860] transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}

              {/* Action buttons — disembunyikan saat terkunci / tak boleh edit */}
              {locked ? (
                <p className="flex items-center gap-1.5 text-[11px] text-[#AEADA8] font-body mt-1">
                  <Lock className="w-3 h-3" /> Terkunci — selesaikan project sebelumnya dulu.
                </p>
              ) : !canEdit ? null : (
              <div className="flex items-center gap-2 mt-1">
                <button
                  onClick={() => setAddingSubtask(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#E0DFD8] text-xs font-body text-[#6B6860] hover:border-[#C8C7BF] hover:text-[#1A1916] hover:bg-[#F8F7F3] transition-all"
                >
                  <Plus className="w-3 h-3" />
                  Tambah Subtask
                </button>
              </div>
              )}
            </div>
          </div>
        )}
      </div>

      {showEdit && <EditTaskModal task={task} onClose={() => setShowEdit(false)} />}
    </>
  )
}
